import * as React from "react";
import { Select } from "@base-ui/react/select";
import { useSeedsPortalContainer } from "./SeedsPortal";
import SelectItem from "./Common/SelectItem";
import type { DataWithId } from "./Common/types";
import { groupItems } from "./Common/groupItems";
import {
  Field,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectGroup,
  SelectGroupLabel,
  SelectSeparator,
  SelectPositioner,
  SelectPopup,
} from "./Common/SelectStyles";
import { StyledChevron, ChevronIcon } from "./Common/SelectIcons";
import { cn } from "./Common/cn";

// ─── CSS-class components (see menu.css) ────────────────────────────────────

const SelectionText = ({
  className,
  ...rest
}: React.ComponentProps<"span">) => (
  <span className={cn("seeds-select-selection-text", className)} {...rest} />
);

// The styles in here need cleaned up, but in MultiSearchableSelect are correct
const Placeholder = ({ className, ...rest }: React.ComponentProps<"div">) => (
  <div className={cn("seeds-select-placeholder", className)} {...rest} />
);

// ─── Props ────────────────────────────────────────────────────────────────────

interface MultiSelectBaseProps {
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  "aria-label"?: string;
  /** Id of a visible label. Preferred over `aria-label`. */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  isInvalid?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

interface MultiSelectDataProps<T extends DataWithId>
  extends MultiSelectBaseProps {
  data: T[];
  itemToString: (item: T) => string;
  renderItem?: (item: T) => React.ReactNode;
  customRenderSelections?: (item: T) => string;
  renderSelection?: (items: T[]) => React.ReactNode;
  inputType?: "icon" | "radio" | "checkbox" | "none";
  maxSelections?: number;
  selectedItemIds?: Array<T["id"]>;
  defaultSelectedItemIds?: Array<T["id"]>;
  onSelectedItemIdsChange?: (ids: Array<T["id"]>) => void;
  groupBy?: (item: T) => string;
  renderGroupHeading?: (label: string) => React.ReactNode;
  removeSelectedItems?: boolean;
  children?: never;
}

interface MultiSelectChildrenProps extends MultiSelectBaseProps {
  children: React.ReactNode;
  selectedItemIds?: Array<string | number>;
  defaultSelectedItemIds?: Array<string | number>;
  onSelectedItemIdsChange?: (ids: Array<string | number>) => void;
  data?: never;
  itemToString?: never;
  renderItem?: never;
  customRenderSelections?: never;
  renderSelection?: never;
  inputType?: never;
  maxSelections?: never;
  groupBy?: never;
  renderGroupHeading?: never;
  removeSelectedItems?: never;
}

export type MultiSelectProps<T extends DataWithId> =
  | MultiSelectDataProps<T>
  | MultiSelectChildrenProps;

// ─── Component ────────────────────────────────────────────────────────────────

export function MultiSelect<T extends DataWithId>(props: MultiSelectProps<T>) {
  const { id, placeholder = "Select...", fullWidth = true } = props;
  const ariaDescribedBy = props["aria-describedby"];
  const ariaLabel = props["aria-label"];
  const ariaLabelledBy = props["aria-labelledby"];
  const isInvalid = props.isInvalid;

  const isChildrenMode = "children" in props && props.children != null;

  const data = isChildrenMode ? [] : (props as MultiSelectDataProps<T>).data;
  const itemToString = isChildrenMode
    ? () => ""
    : (props as MultiSelectDataProps<T>).itemToString;
  const renderItem = isChildrenMode
    ? undefined
    : (props as MultiSelectDataProps<T>).renderItem;
  const customRenderSelections = isChildrenMode
    ? undefined
    : (props as MultiSelectDataProps<T>).customRenderSelections;
  const maxSelections = isChildrenMode
    ? undefined
    : (props as MultiSelectDataProps<T>).maxSelections;
  const renderSelection = isChildrenMode
    ? undefined
    : (props as MultiSelectDataProps<T>).renderSelection;
  const removeSelectedItems = isChildrenMode
    ? false
    : (props as MultiSelectDataProps<T>).removeSelectedItems ?? false;
  const inputType = isChildrenMode
    ? "checkbox"
    : removeSelectedItems
    ? "none"
    : (props as MultiSelectDataProps<T>).inputType ?? "checkbox";
  const selectedItemIds = props.selectedItemIds;
  const defaultSelectedItemIds = props.defaultSelectedItemIds;
  const onSelectedItemIdsChange = props.onSelectedItemIdsChange;
  const groupBy = isChildrenMode
    ? undefined
    : (props as MultiSelectDataProps<T>).groupBy;
  const renderGroupHeading = isChildrenMode
    ? undefined
    : (props as MultiSelectDataProps<T>).renderGroupHeading;
  const children = isChildrenMode
    ? (props as MultiSelectChildrenProps).children
    : undefined;

  const { containerRef, portalContainer } = useSeedsPortalContainer();

  const isControlled = selectedItemIds !== undefined;

  const [internalValue, setInternalValue] = React.useState<string[]>(
    () => defaultSelectedItemIds?.map(String) ?? []
  );

  const value = isControlled ? selectedItemIds!.map(String) : internalValue;

  const items = React.useMemo(
    () =>
      data.map((item) => ({
        value: String(item.id),
        label: itemToString(item),
      })),
    [data, itemToString]
  );

  const selectedItems = React.useMemo(
    () =>
      value
        .map((v) => data.find((d) => String(d.id) === v))
        .filter((d): d is T => d != null),
    [data, value]
  );

  function handleValueChange(newValues: string[]) {
    if (!isControlled) {
      setInternalValue(newValues);
    }
    if (onSelectedItemIdsChange) {
      const ids = newValues.map((v) => {
        const item = data.find((d) => String(d.id) === v);
        return item ? item.id : v;
      });
      onSelectedItemIdsChange(ids as Array<T["id"]>);
    }
  }

  return (
    <Field $fullWidth={fullWidth}>
      <div ref={containerRef} style={{ position: "relative" }} />
      <Select.Root
        multiple
        items={items}
        value={value}
        onValueChange={handleValueChange}
        disabled={props.disabled}
        id={id}
      >
        <SelectTrigger
          aria-describedby={ariaDescribedBy}
          aria-invalid={isInvalid || undefined}
          aria-required={props.required || undefined}
          $isInvalid={isInvalid}
        >
          <SelectValue>
            {() => {
              if (renderSelection) return renderSelection(selectedItems);
              if (selectedItems.length === 0)
                return <Placeholder>{placeholder}</Placeholder>;

              const visibleItems =
                maxSelections != null
                  ? selectedItems.slice(0, maxSelections)
                  : selectedItems;
              const overflowCount =
                maxSelections != null && selectedItems.length > maxSelections
                  ? selectedItems.length - maxSelections
                  : 0;

              const text = visibleItems
                .map((item) =>
                  customRenderSelections
                    ? customRenderSelections(item)
                    : itemToString(item)
                )
                .join(", ");

              return (
                <SelectionText>
                  {text}
                  {overflowCount > 0 ? `, +${overflowCount}` : ""}
                </SelectionText>
              );
            }}
          </SelectValue>
          <SelectIcon>
            <StyledChevron data-chevron>
              <ChevronIcon />
            </StyledChevron>
          </SelectIcon>
        </SelectTrigger>
        <Select.Portal container={portalContainer}>
          <SelectPositioner sideOffset={8} alignItemWithTrigger={false}>
            <SelectPopup>
              <Select.ScrollUpArrow />
              <Select.List
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
              >
                {children
                  ? children
                  : groupBy
                  ? groupItems(data, groupBy).map((group, index, arr) => (
                      <React.Fragment key={group.value}>
                        <SelectGroup>
                          <SelectGroupLabel>
                            {renderGroupHeading
                              ? renderGroupHeading(group.value)
                              : group.value}
                          </SelectGroupLabel>
                          {group.items.map((item) => (
                            <SelectItem
                              key={item.id}
                              value={String(item.id)}
                              label={itemToString(item)}
                              disabled={item.disabled}
                              inputType={inputType}
                              hidden={
                                removeSelectedItems &&
                                value.includes(String(item.id))
                              }
                              renderItem={
                                renderItem ? () => renderItem(item) : undefined
                              }
                            />
                          ))}
                        </SelectGroup>
                        {index < arr.length - 1 && <SelectSeparator />}
                      </React.Fragment>
                    ))
                  : data.map((item) => (
                      <SelectItem
                        key={item.id}
                        value={String(item.id)}
                        label={itemToString(item)}
                        disabled={item.disabled}
                        inputType={inputType}
                        hidden={
                          removeSelectedItems && value.includes(String(item.id))
                        }
                        renderItem={
                          renderItem
                            ? () => renderItem(item)
                            : () => itemToString(item)
                        }
                      />
                    ))}
              </Select.List>
              <Select.ScrollDownArrow />
            </SelectPopup>
          </SelectPositioner>
        </Select.Portal>
      </Select.Root>
    </Field>
  );
}
